import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { friendsChanged } from "@/lib/friends";
import { toast } from "sonner";
import { UserPlus, Check, ChatCircleDots, X } from "@phosphor-icons/react";

const btn =
  "inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-xs font-bold uppercase tracking-wider disabled:opacity-50";
const primary = `${btn} bg-brand text-brand-foreground hover:bg-brand-hover`;
const quiet = `${btn} border border-border text-muted-foreground hover:text-foreground`;

// Add friend / Request sent / Accept / Message, on another player's profile.
export default function FriendButton({ userId, username }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    setStatus(null);
    api.get(`/friends/with/${userId}`)
      .then(({ data }) => live && setStatus(data.status))
      .catch(() => live && setStatus("none"));
    return () => { live = false; };
  }, [userId]);

  const act = async (fn, done) => {
    setBusy(true);
    try {
      const { data } = await fn();
      setStatus(data.status);
      if (done) toast.success(done(data.status));
      friendsChanged();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  if (!status || status === "self") return null;

  if (status === "friends") {
    return (
      <div className="flex flex-wrap items-center gap-2" data-testid="friend-status-friends">
        <button type="button" className={primary} onClick={() => navigate(`/friends/${userId}`)} data-testid="friend-message">
          <ChatCircleDots size={15} weight="bold" /> Message
        </button>
        <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">
          <Check size={14} weight="bold" /> Friends
        </span>
      </div>
    );
  }

  if (status === "incoming") {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground w-full">{username} wants to be friends.</span>
        <button
          type="button"
          disabled={busy}
          className={primary}
          onClick={() => act(() => api.post(`/friends/${userId}/accept`), () => `You and ${username} are friends`)}
          data-testid="friend-accept"
        >
          <Check size={15} weight="bold" /> Accept
        </button>
        <button
          type="button"
          disabled={busy}
          className={quiet}
          onClick={() => act(() => api.post(`/friends/${userId}/decline`))}
          data-testid="friend-decline"
        >
          <X size={15} weight="bold" /> Decline
        </button>
      </div>
    );
  }

  if (status === "outgoing") {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-muted-foreground" data-testid="friend-requested">
          <Check size={14} weight="bold" /> Request sent
        </span>
        <button
          type="button"
          disabled={busy}
          className="text-xs font-bold uppercase tracking-wider text-muted-foreground hover:underline"
          onClick={() => act(() => api.delete(`/friends/${userId}`))}
          data-testid="friend-cancel"
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      disabled={busy}
      className={primary}
      onClick={() => act(
        () => api.post(`/friends/${userId}/request`),
        (s) => (s === "friends" ? `You and ${username} are friends` : `Friend request sent to ${username}`),
      )}
      data-testid="friend-add"
    >
      <UserPlus size={15} weight="bold" /> Add friend
    </button>
  );
}
